import React, { Suspense } from "react";
import { ApplicationFeature, UserRole } from "@green0meter/api-client";

import CsrdHubPage from "../../pages/csrd";
import CsrdFormPage from "../../pages/csrd/form";
import CsrdDashboardPage from "../../pages/csrd/dashboard";
import BrsrHubPage from "../../pages/brsr";
import BrsrFormPage from "../../pages/brsr/form";
import BrsrDashboardPage from "../../pages/brsr/dashboard";
import CsobSkHubPage from "../../pages/csob-sk";
import CsobSkFormPage from "../../pages/csob-sk/form";

import { Loader, RequireFeature, RequireRole } from "@components/common";
import { UserLayout } from "@layouts";
import {
  AccountSettingsPage,
  BuyersHubPage,
  Co2DashboardPage,
  Co2FormPage,
  Co2HubPage,
  ComponentsPage,
  CsobHubPage,
  DatasetComparisonPage,
  DocumentDetailPage,
  DocumentManagementHubPage,
  DoubleMaterialityPage,
  DoubleMaterialityAnswersPage,
  EnginesPage,
  FormBuilderDetailPage,
  FormBuilderPage,
  HomePage,
  OrganizationPage,
  PaygapHubPage,
  QuestionsAssignmentPage,
  RecommendationPage,
  AdminHubPage,
  ServicesMarketplacePage,
  ServicePage,
  SupplierFormPage,
  SuppliersHubPage,
  EmissionFactorsPage,
  VsmeHubPage,
  VsmeFormPage,
  VsmeDashboardPage,
} from "@pages";

export const userRoutes = [
  {
    element: (
      <Suspense fallback={<Loader />}>
        <UserLayout />
      </Suspense>
    ),
    children: [
      {
        index: true,
        element: <HomePage />,
      },
      {
        path: "/co2",
        element: (
          <RequireFeature features={[ApplicationFeature.CO2]}>
            <Co2HubPage />
          </RequireFeature>
        ),
      },
      {
        path: "/paygap",
        element: (
          <RequireFeature features={[ApplicationFeature.PAYGAP]}>
            <PaygapHubPage />
          </RequireFeature>
        ),
      },
      {
        path: "/co2/form/:formId",
        element: (
          <RequireFeature features={[ApplicationFeature.CO2]}>
            <Co2FormPage />
          </RequireFeature>
        ),
      },
      {
        path: "/co2/dashboard",
        element: (
          <RequireFeature features={[ApplicationFeature.CO2]}>
            <Co2DashboardPage />
          </RequireFeature>
        ),
      },
      {
        path: "/co2/recommendation/:recommendationId",
        element: (
          <RequireFeature features={[ApplicationFeature.CO2]}>
            <RecommendationPage />
          </RequireFeature>
        ),
      },
      {
        path: "/vsme",
        element: (
          <RequireFeature features={[ApplicationFeature.VSME]}>
            <VsmeHubPage />
          </RequireFeature>
        ),
      },
      {
        path: "/vsme/form/:formId",
        element: (
          <RequireFeature features={[ApplicationFeature.VSME]}>
            <VsmeFormPage />
          </RequireFeature>
        ),
      },
      {
        path: "/vsme/dashboard",
        element: (
          <RequireFeature features={[ApplicationFeature.VSME]}>
            <VsmeDashboardPage />
          </RequireFeature>
        ),
      },
      {
        path: "/suppliers-hub/:procurementId/form/:formId",
        element: (
          <RequireFeature features={[ApplicationFeature.PROCUREMENT_SUPPLIER]}>
            <SupplierFormPage />
          </RequireFeature>
        ),
      },
      {
        path: "/document-management-hub",
        element: <DocumentManagementHubPage />,
      },
      {
        path: "/document-management-hub/:fileId",
        element: <DocumentDetailPage />,
      },
      {
        path: "/services-marketplace",
        element: (
          <RequireRole roles={[UserRole.Owner, UserRole.Manager]}>
            <ServicesMarketplacePage />
          </RequireRole>
        ),
      },
      {
        path: "/services-marketplace/:serviceId",
        element: (
          <RequireRole roles={[UserRole.Owner, UserRole.Manager]}>
            <ServicePage />
          </RequireRole>
        ),
      },
      {
        path: "/csrd",
        element: (
          <RequireFeature features={[ApplicationFeature.CSRD]}>
            <CsrdHubPage />
          </RequireFeature>
        ),
      },
      {
        path: "/admin/:tab",
        element: (
          <RequireRole roles={[UserRole.Admin]}>
            <AdminHubPage />
          </RequireRole>
        ),
      },
      {
        path: "/csrd/form/:formId",
        element: (
          <RequireFeature features={[ApplicationFeature.CSRD]}>
            <CsrdFormPage />
          </RequireFeature>
        ),
      },
      {
        path: "/csrd/dashboard",
        element: (
          <RequireFeature features={[ApplicationFeature.CSRD]}>
            <CsrdDashboardPage />
          </RequireFeature>
        ),
      },
      {
        path: "/csrd/double-materiality",
        element: (
          <RequireFeature features={[ApplicationFeature.CSRD]}>
            <DoubleMaterialityPage />
          </RequireFeature>
        ),
      },
      {
        path: "/csrd/double-materiality/answers/:stakeholderId",
        element: (
          <RequireFeature features={[ApplicationFeature.CSRD]}>
            <DoubleMaterialityAnswersPage />
          </RequireFeature>
        ),
      },
      {
        path: "/brsr",
        element: (
          <RequireFeature features={[ApplicationFeature.BRSR]}>
            <BrsrHubPage />
          </RequireFeature>
        ),
      },
      {
        path: "/brsr/form/:formId",
        element: (
          <RequireFeature features={[ApplicationFeature.BRSR]}>
            <BrsrFormPage />
          </RequireFeature>
        ),
      },
      {
        path: "/brsr/dashboard",
        element: (
          <RequireFeature features={[ApplicationFeature.BRSR]}>
            <BrsrDashboardPage />
          </RequireFeature>
        ),
      },
      {
        path: "/csob",
        element: (
          <RequireFeature features={[ApplicationFeature.CSOB]}>
            <CsobHubPage />
          </RequireFeature>
        ),
      },
      {
        path: "/csob/dataset-comparison",
        element: (
          <RequireFeature features={[ApplicationFeature.CSOB]}>
            <DatasetComparisonPage />
          </RequireFeature>
        ),
      },
      {
        path: "/csob-sk",
        element: (
          <RequireFeature features={[ApplicationFeature.CSOB_SK]}>
            <CsobSkHubPage />
          </RequireFeature>
        ),
      },
      {
        path: "/csob-sk/form/:formId",
        element: (
          <RequireFeature features={[ApplicationFeature.CSOB_SK]}>
            <CsobSkFormPage />
          </RequireFeature>
        ),
      },
      {
        path: "/buyers-hub",
        element: (
          <RequireRole
            roles={[
              UserRole.Owner,
              UserRole.Manager,
              UserRole.Viewer,
              UserRole.Auditor,
            ]}
          >
            <RequireFeature features={[ApplicationFeature.PROCUREMENT_BUYER]}>
              <BuyersHubPage />
            </RequireFeature>
          </RequireRole>
        ),
      },
      {
        path: "/suppliers-hub/:procurementId",
        element: (
          <RequireFeature features={[ApplicationFeature.PROCUREMENT_SUPPLIER]}>
            <SuppliersHubPage />
          </RequireFeature>
        ),
      },
      {
        path: "/organization/:tab",
        element: (
          <RequireRole roles={[UserRole.Owner, UserRole.Manager]}>
            <OrganizationPage />
          </RequireRole>
        ),
      },
      {
        path: "/form-builder",
        element: (
          <RequireRole roles={[UserRole.Owner]}>
            <FormBuilderPage />
          </RequireRole>
        ),
      },
      {
        path: "/form-builder/:formId",
        element: (
          <RequireRole roles={[UserRole.Owner]}>
            <FormBuilderDetailPage />
          </RequireRole>
        ),
      },
      {
        path: "/account-settings",
        element: <AccountSettingsPage />,
      },
      {
        path: "/emission-factors",
        element: (
          <RequireRole roles={[UserRole.Owner, UserRole.Manager]}>
            <EmissionFactorsPage />
          </RequireRole>
        ),
      },
      {
        path: "/engines",
        element: <EnginesPage />,
      },
      {
        path: "/question-assignment/:formId?",
        element: (
          <RequireRole roles={[UserRole.Owner]}>
            <QuestionsAssignmentPage />
          </RequireRole>
        ),
      },
      {
        element: <ComponentsPage />,
        path: "components",
      },
    ],
  },
];
